import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Box,
  Hidden,
  CardContent,
  CardHeader,
  Card,
  Divider,
  Alert,
  AlertColor,
  Toolbar,
  Button
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { connect } from 'react-redux';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import FleetIoService from '../services/FleetIoService';
import { formatErrorMessage, getDaysFromNow } from '../utils/common';
import { hide, show } from '../store/actions/busyIndicatorActions';
import GlobalSearch from '../components/GlobalSearch';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
  fleetIoId: number,
  stateId: number,
  odometer: number
}

export interface CustomFields {
  hours: string;
}

export interface PrimaryMeterEntry {
  id: number;
  auto_voided_at: any;
  category: any;
  date: string;
  meter_type: any;
  meterable_id: number;
  meterable_type: string;
  value: string;
  vehicle_id: number;
  void: boolean;
  type: any;
  created_at: string;
  updated_at: string;
}

export interface AttachmentPermissions {}

export interface Label {
  id: number;
  name: string;
}

export interface Vehicle {
  id: number;
  name: string;
  color: string;
  license_plate: string;
  make: string;
  model: string;
  registration_expiration_month: number;
  registration_state: string;
  trim?: string;
  vin: string;
  year: number;
  default_image_url_small: any;
}

export interface Vendor {
  id: number;
  city: any;
  country?: string;
  external_id: any;
  name: string;
  phone: any;
  postal_code: any;
  region: any;
}

export interface ServiceEntry {
  id: number;
  reference: string;
  vendor_id?: number;
  vendor_name?: string;
  vehicle_id: number;
  images_count: number;
  documents_count: number;
  comments_count: number;
  work_order_id: any;
  general_notes: string;
  is_sample: boolean;
  status: string;
  auto_integrate_repair_order_status: any;
  created_at: string;
  updated_at: string;
  labor_time_in_seconds: any;
  custom_fields: CustomFields;
  started_at: any;
  completed_at: string;
  primary_meter_entry?: PrimaryMeterEntry;
  attachment_permissions: AttachmentPermissions;
  labels: Label[];
  vehicle: Vehicle;
  vendor?: Vendor;
}

const VehicleServiceEntries: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
  fleetIoId,
  stateId,
  odometer
}) => {
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});
  const [remindersData, setRemindersData] = useState<ServiceEntry[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filterByCol, setFilterByCol] = useState('all_col');
  const [filteredRecords, setFilteredRecords] = useState<ServiceEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>('');
  const [loadingMore, setLoadingMore] = useState(false);

  // Add more columns for additional ServiceEntry fields
  const columns = [
    { field: 'vehicle', headerName: 'Vehicle' },
    { field: 'reference', headerName: 'Reference' },
    { field: 'status', headerName: 'Status' },
    { field: 'created_at', headerName: 'Created At' },
    { field: 'updated_at', headerName: 'Updated At' },
    { field: 'vendor_name', headerName: 'Vendor' },
    { field: 'images_count', headerName: 'Images' },
    { field: 'documents_count', headerName: 'Documents' },
    { field: 'comments_count', headerName: 'Comments' },
    { field: 'is_sample', headerName: 'Sample?' },
    { field: 'work_order_id', headerName: 'Work Order' },
    { field: 'labor_time_in_seconds', headerName: 'Labor Time (s)' },
    { field: 'completed_at', headerName: 'Completed At' }
  ];

  // Update getColValue to match new ServiceEntry fields
  const getColValue = (record: ServiceEntry, col: string) => {
    switch (col) {
      case 'vehicle':
        return record.vehicle?.name || '';
      case 'reference':
        return record.reference || '';
      case 'status':
        return record.status || '';
      case 'created_at':
        return record.created_at ? new Date(record.created_at).toLocaleString() : '';
      case 'updated_at':
        return record.updated_at ? new Date(record.updated_at).toLocaleString() : '';
      case 'vendor_name':
        return record.vendor_name || '';
      case 'images_count':
        return record.images_count;
      case 'documents_count':
        return record.documents_count;
      case 'comments_count':
        return record.comments_count;
      case 'is_sample':
        return record.is_sample ? 'Yes' : 'No';
      case 'work_order_id':
        return record.work_order_id || '';
      case 'labor_time_in_seconds':
        return record.labor_time_in_seconds || '';
      case 'completed_at':
        return record.completed_at ? new Date(record.completed_at).toLocaleString() : '';
      default:
        return '';
    }
  };

  const onSearchTextChange = (event: any) => {
    const text = event.target.value.toLowerCase();
    setSearchText(text);

    if (!text) {
      setFilteredRecords(remindersData || []);
      return;
    }

    const filtered = (remindersData || []).filter((record: ServiceEntry) => {
      // Filter only via selected column
      if (filterByCol !== 'all_col') {
        return getColValue(record, filterByCol).toLowerCase().includes(text);
      }

      // Filter over all searchable fields (update to match new fields)
      return (
        (record.vehicle?.name || '').toLowerCase().includes(text)
        || (record.reference || '').toLowerCase().includes(text)
        || (record.status || '').toLowerCase().includes(text)
        || (record.vendor_name || '').toLowerCase().includes(text)
        || (record.vehicle?.license_plate || '').toLowerCase().includes(text)
        || (record.created_at ? new Date(record.created_at).toLocaleString() : '').toLowerCase().includes(text)
        || (record.updated_at ? new Date(record.updated_at).toLocaleString() : '').toLowerCase().includes(text)
      );
    });

    setFilteredRecords(filtered);
  };

  const onFilterByColumnChange = (col: string) => {
    setFilterByCol(col);
    // Re-run search with new column filter
    onSearchTextChange({ target: { value: searchText } });
  };

  const handleRowClick = (id: number) => {
    setOpenRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const refreshData = () => {
    showBusyIndicator();
    const payload = {
      actionId: 801,
      stateId,
      queryParams: {
        start_cursor: ''
      }
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp: any) => {
        const filtered = resp?.records?.filter((entry: ServiceEntry) => entry.vehicle?.id === fleetIoId) || [];
        setRemindersData(filtered);
        setFilteredRecords(filtered);
        setNextCursor(resp?.next_cursor || null);
        hideBusyIndicator();
      }).catch((err: any) => {
        hideBusyIndicator();
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  const loadMore = () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    const payload = {
      actionId: 801,
      stateId,
      queryParams: {
        start_cursor: nextCursor
      }
    };
    FleetIoService.fleetIoPage(payload)
      .then((resp: any) => {
        const filtered = resp?.records?.filter((entry: ServiceEntry) => entry.vehicle?.id === fleetIoId) || [];
        setRemindersData((prev) => [...prev, ...filtered]);
        setFilteredRecords((prev) => [...prev, ...filtered]);
        setNextCursor(resp?.next_cursor || null);
        setLoadingMore(false);
      }).catch((err: any) => {
        setLoadingMore(false);
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  useEffect(() => {
    refreshData();
  }, []);

  return (
    <>
      {remindersData && remindersData.length > 0 && (
        <div>
          <Toolbar style={{ justifyContent: 'flex-end' }}>
            <GlobalSearch
              searchedText={searchText}
              onSearchTextChange={onSearchTextChange}
              showFilterByColumn
              columns={columns}
              filterByColumn={filterByCol}
              onFilterByColumnChange={onFilterByColumnChange}
              width="300px"
            />
          </Toolbar>

          <Hidden mdDown>
            {/* Desktop/Table Layout */}
            <TableContainer component={Paper} elevation={3} sx={{ mt: 2, borderRadius: 2 }}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell />
                    <TableCell><strong>Vehicle</strong></TableCell>
                    <TableCell><strong>Reference</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Created At</strong></TableCell>
                    <TableCell><strong>Updated At</strong></TableCell>
                    <TableCell><strong>Vendor</strong></TableCell>
                    <TableCell><strong>Sample?</strong></TableCell>
                    <TableCell><strong>Work Order</strong></TableCell>
                    <TableCell><strong>Labor Time (s)</strong></TableCell>
                    <TableCell><strong>Completed At</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.length > 0 ? (
                    filteredRecords.map((record) => (
                      <React.Fragment key={record.id}>
                        <TableRow>
                          <TableCell>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => handleRowClick(record.id)}
                            >
                              {openRows[record.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{record.vehicle?.name}</TableCell>
                          <TableCell>{record.reference}</TableCell>
                          <TableCell>{record.status}</TableCell>
                          <TableCell>{record.created_at ? new Date(record.created_at).toLocaleString() : ''}</TableCell>
                          <TableCell>{record.updated_at ? new Date(record.updated_at).toLocaleString() : ''}</TableCell>
                          <TableCell>{record.vendor_name || (record.vendor?.name || '--')}</TableCell>
                          <TableCell>{record.is_sample ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{record.work_order_id || ''}</TableCell>
                          <TableCell>{record.labor_time_in_seconds || ''}</TableCell>
                          <TableCell>{record.completed_at ? new Date(record.completed_at).toLocaleString() : ''}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14}>
                            <Collapse in={openRows[record.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <Typography variant="body2"><strong>Vehicle Details:</strong> {record.vehicle?.make} {record.vehicle?.model} ({record.vehicle?.year}) - {record.vehicle?.license_plate}</Typography>
                                <Typography variant="body2"><strong>General Notes:</strong> {record.general_notes}</Typography>
                                <Typography variant="body2"><strong>Labels:</strong> {record.labels && record.labels.length > 0 ? record.labels.map((l) => l.name).join(', ') : '--'}</Typography>
                                <Typography variant="body2"><strong>Primary Meter Entry:</strong> {record.primary_meter_entry?.value ? record.primary_meter_entry?.value : '--'}</Typography>
                                <Typography variant="body2"><strong>Created At:</strong> {record.created_at ? new Date(record.created_at).toLocaleString() : ''}</Typography>
                                <Typography variant="body2"><strong>Updated At:</strong> {record.updated_at ? new Date(record.updated_at).toLocaleString() : ''}</Typography>
                                <Typography variant="body2"><strong>Completed At:</strong> {record.completed_at ? new Date(record.completed_at).toLocaleString() : ''}</Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={14} align="center">
                        <Typography variant="body1" sx={{ py: 2 }}>No entries found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {nextCursor && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                {/* <button onClick={loadMore} type="button" variant="filled" disabled={loadingMore} style={{ padding: '8px 24px', fontSize: '16px', borderRadius: '4px', border: '1px solid #1976d2', background: '#1976d2', color: '#fff', cursor: loadingMore ? 'not-allowed' : 'pointer' }}>
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button> */}
                <Button
                  disabled={loadingMore}
                  onClick={loadMore}
                  color="primary"
                  variant="outlined"
                  id="cancelFetchBtn"
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </Hidden>

          <Hidden mdUp>
            {/* Mobile/Responsive Layout */}
            {filteredRecords.length > 0 ? (
              filteredRecords.map((record) => (
                <Card key={record.id} sx={{ mb: 2 }}>
                  <CardHeader
                    title={`Vehicle: ${record.vehicle?.name}`}
                    subheader={`Reference: ${record.reference}`}
                    action={(
                      <IconButton onClick={() => handleRowClick(record.id)}>
                        {openRows[record.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    )}
                  />
                  <Divider />
                  <CardContent>
                    <Typography variant="body2"><strong>Status:</strong> {record.status}</Typography>
                    <Typography variant="body2"><strong>Vendor:</strong> {record.vendor_name || (record.vendor?.name || '--')}</Typography>
                    <Typography variant="body2"><strong>Images:</strong> {record.images_count}</Typography>
                    <Typography variant="body2"><strong>Documents:</strong> {record.documents_count}</Typography>
                    <Typography variant="body2"><strong>Comments:</strong> {record.comments_count}</Typography>
                    <Typography variant="body2"><strong>Sample?:</strong> {record.is_sample ? 'Yes' : 'No'}</Typography>
                    <Typography variant="body2"><strong>Work Order:</strong> {record.work_order_id || ''}</Typography>
                    <Typography variant="body2"><strong>Labor Time (s):</strong> {record.labor_time_in_seconds || ''}</Typography>
                    <Typography variant="body2"><strong>Created At:</strong> {record.created_at ? new Date(record.created_at).toLocaleString() : ''}</Typography>
                    <Typography variant="body2"><strong>Updated At:</strong> {record.updated_at ? new Date(record.updated_at).toLocaleString() : ''}</Typography>
                    <Typography variant="body2"><strong>Completed At:</strong> {record.completed_at ? new Date(record.completed_at).toLocaleString() : ''}</Typography>
                    <Collapse in={openRows[record.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6">Details</Typography>
                        <Typography variant="body2"><strong>Vehicle Details:</strong> {record.vehicle?.make} {record.vehicle?.model} ({record.vehicle?.year}) - {record.vehicle?.license_plate}</Typography>
                        <Typography variant="body2"><strong>General Notes:</strong> {record.general_notes}</Typography>
                        <Typography variant="body2"><strong>Custom Fields:</strong> {record.custom_fields ? JSON.stringify(record.custom_fields) : '--'}</Typography>
                        <Typography variant="body2"><strong>Labels:</strong> {record.labels && record.labels.length > 0 ? record.labels.map((l) => l.name).join(', ') : '--'}</Typography>
                        <Typography variant="body2"><strong>Primary Meter Entry:</strong> {record.primary_meter_entry ? JSON.stringify(record.primary_meter_entry) : '--'}</Typography>
                        <Typography variant="body2"><strong>Attachment Permissions:</strong> {record.attachment_permissions ? JSON.stringify(record.attachment_permissions) : '--'}</Typography>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography variant="body1" sx={{ py: 2 }}>No entries found.</Typography>
            )}
          </Hidden>
        </div>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts()),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
});

export default connect(null, mapDispatchToProps)(VehicleServiceEntries);
